import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import * as Constants from 'common/helpers/constants';
import { KeyboardKey } from 'common/helpers/keyboard-keys';
import { useEffect, useRef, useState } from 'react';

const ComponentContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'nowrap',
  margin: theme.spacing(0, 6, 0, 3)
}));
const NameFields = styled('div')(() => ({
  alignItems: 'center',
  display: 'grid',
  gridTemplateColumns: '1fr min-content min-content'
}));

export type ProfileNameInputProps = {
  className?: string;
  name: string;
  draftName?: string;
  error?: boolean;
  isLoading?: boolean;
  onStartEditing?: () => void;
  onDraftChange?: (draft: string) => void;
  onCommit?: (finalName: string) => void;
  onCancel?: () => void;
}

export default function ProfileNameInput(props: Readonly<ProfileNameInputProps>) {
  const {
    className,
    error,
    isLoading,
    name,
    draftName,
    onStartEditing,
    onDraftChange,
    onCommit,
    onCancel
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  const [isEditingName, setIsEditingName] = useState<boolean>(false);

  useEffect(() => {
    if (
      name !== Constants.defaultProfileName ||
      !isEditingName ||
      !inputRef.current
    ) return;

    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [isEditingName]);

  const value = draftName ?? name;

  return (
    <ComponentContainer className={className}>
      {!isEditingName && (
        <Typography variant="h4" color={error ? 'error' : 'unset'}>
          {isLoading ? "Loading..." : props.name}
        </Typography>
      )}
      {isEditingName && (
        <NameFields>
          <TextField
            id="name"
            variant="outlined"
            slotProps={{ inputLabel: { shrink: Boolean(name) } }}
            label="Team Name"
            value={value}
            inputRef={inputRef}
            onChange={(event) => onDraftChange?.(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === KeyboardKey.Enter) {
                setIsEditingName(false);
                onCommit?.(value);
              }
              if (event.key === KeyboardKey.Escape) {
                setIsEditingName(false);
                onCancel?.();
              }
            }}
          />
          <Tooltip title="Accept Change">
            <span>
              <IconButton 
                onClick={() => {
                  setIsEditingName(false);
                  onCommit?.(value);
                }}
                color="success"
                disabled={
                  isLoading || 
                  value.trim().length === 0 || 
                  value === name
                }
                size="medium"
                aria-label='accept change'
              >
                <CheckIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel">
            <span>
              <IconButton 
                onClick={() => {
                  setIsEditingName(false);
                  onCancel?.();
                }}
                color="error"
                disabled={isLoading}
                aria-label="cancel"
                size="medium"
                edge="end"
              >
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        </NameFields>
      )}
      {!isEditingName && (
        <Tooltip title="Edit Name">
          <span>
            <IconButton
              onClick={() => {
                onStartEditing?.();
                setIsEditingName(true);
              }}
              color="primary"
              disabled={isLoading}
              aria-label="edit name"
              size="medium"
              edge="end">
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </ComponentContainer>
  );
}
